
import React, { useEffect, useState } from 'react'
import { Row, Col, Alert, Form, FormCheck, Badge, CloseButton } from 'react-bootstrap'
import Card from '../../../components/Card'

import { ProfitItem } from '../../../features/profit';
import { getProfit } from '../../../services/api/profit';
import { ApiError, Profit } from '../../../services/openapi';

import { useKeycloak } from '@react-keycloak/web'

import SearchInput from '../../../components/SearchInput';
import ListPagination from '../../../components/ListPagination';
import RefreshPull from '../../../components/RefreshPull';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { useTranslation } from 'react-i18next'; 
//import CsvExport from '../../../components/exports/CsvExport';

const ProfitView = () => {
    const [profitLines, setProfitLines] = useState<Profit[]>([]);    
    const [profitLinesCount, setProfitLinesCount] = useState<string>("0");    
    const [pagingString, setPagingString] = useState<string>("0-25");        
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    const [tableBodyHeight, setTableBodyHeight] = useState<number>(380);    
    const [formData, setFormData] = useLocalStorage<string>("profit","formData", "");
    const [searchData, setSearchData] = useLocalStorage<Record<string, string>>("profit", "searchData", {});
    const [filterData, setFilterData] = useLocalStorage<string>("profit", "filterData", "");
    const [currentPage, setCurrentPage] = useLocalStorage<number>("profit", "currentPage", 0);
    const [siteFilter, setSiteFilter] = useLocalStorage<number[]>("sites", "siteFilter", []);
    const [siteFilterQuery, setSiteFilterQuery] = useLocalStorage<string>("sites", "siteFilterQuery", "");
    const [errorMsg, setErrorMsg] = useState<string[]>([]);

    const { t } = useTranslation();
    const { keycloak, initialized } = useKeycloak();

    const delay = async (ms: number) => {
        await new Promise(resolve => setTimeout(resolve, ms));
    }

    const csvHeaders = [
        { label: 'POI', key: 'poi' },
        { label: 'Terminal', key: 'description' },
        { label: t('csv.created'), key: 'created' },
        { label: 'Site ID', key: 'siteId' },
        { label: t('csv.txCount'), key: 'txCount' },
        { label: t('csv.lastTransaction'), key: 'txDate' },
    ];

    let timer: NodeJS.Timeout | null = null;

    const submitSearch = () => {
        formData === "" ? setFormData(" ") : setFormData("");
    }

    const handleChange = () => {
        if (timer) {
            clearTimeout(timer)
        }
        timer = setTimeout(submitSearch, 2000);
        setCurrentPage(0);        
    }

    const handlePaging = async (e: number) => {
        errorMsg.length = 0;

        do {
            await delay(1000);
        } while (isRefreshing);
        
        setCurrentPage(e);

        const start = ((e + 1) * 25) - 25;
        const end = ((e + 1) * 25);
        const rangeString = `${start}-${end}`
        setPagingString(rangeString);

        document.querySelector('#refresh')?.classList.remove("d-none")
        setIsRefreshing(true);

        getProfit(keycloak.token || '', rangeString, { search: formData, profitViewType: filterData, sites: siteFilter.join(), sitesQuery: siteFilterQuery }, searchData)
            .then((result) => {
                setProfitLines(result.body[0]);
                var totalCount = result.header.split('/')[1];
                setProfitLinesCount(totalCount);
                document.querySelector('#refresh')?.classList.add("d-none");
                setIsRefreshing(false);
            })
            .catch((error: ApiError) => {
                try {
                    document.querySelector('.errorMsg')?.classList.remove("d-none");
                    Object.entries<Record<string, any>>(JSON.parse(error.body)["errors"]).forEach((k) => { k[1].forEach((msg: string) => { errorMsg.push(msg); setErrorMsg(errorMsg); }) });
                }
                catch (ex) {
                    errorMsg.push("Error handling response");
                    setErrorMsg(errorMsg);
                }

                document.querySelector('#refresh')?.classList.add("d-none");
                setIsRefreshing(false);
            });
    }

    //const handleCsvExport = (): Promise<ApiResult> => {
    //    const rangeString = "0";
    //    return getTxCount(keycloak.token || '', rangeString, { search: formData }, searchData);
    //}

    const handleFilter = (e: string): void => {
        setProfitLines([]);
        setFilterData(e);
        setCurrentPage(0);
    }

    const handleSearch = (e: string): void => {
        setProfitLines([]);
        searchData["search"] = e;
        setSearchData(searchData);
        handleChange();
    }

    const handleSearchColumn1 = (e: string): void => {
        setProfitLines([]);
        searchData["period"] = e;
        setSearchData(searchData);
        handleChange();
    }

    const handleSearchColumn2 = (e: string): void => {
        setProfitLines([]);
        searchData["succeeded"] = e;
        setSearchData(searchData);
        handleChange();
    }

    const handleSearchColumn3 = (e: string): void => {
        setProfitLines([]);
        searchData["failed"] = e;
        setSearchData(searchData);
        handleChange();
    }
    const handleSearchColumn4 = (e: string): void => {
        setProfitLines([]);
        searchData["profitInCents"] = e;
        setSearchData(searchData);
        handleChange();
    }

    const handleRefreshButton = (): void => {
        setProfitLines([]);
        formData === "" ? setFormData(" ") : setFormData("");
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>): boolean => {
        if (event.key === 'Enter') {
            event.preventDefault();
            return false;
        }
        return true;
    }

    const init = () => {
        document.querySelector('.errorMsg')?.classList.add("d-none");
        handlePaging(currentPage ?? 0);
        initSearchFields();
    }; 

    const initSearchFields = () => {
        Object.keys(searchData).map(x => {
            const search: HTMLInputElement | null = document.querySelector(`.${x}`) as HTMLInputElement;
            if (search) {
                search.value = searchData[x];
            }
        })
    }

    const onResize = () => {
        var e = document.querySelector(".vertical-scrollable") as HTMLElement;
        var h = window.innerHeight;
        e.style.height = (h - 284) + "px";
        e.style.maxHeight = (h - 280) + "px";
        setTableBodyHeight(h - 284);        
    };

    useEffect(() => {
        window.addEventListener("resize", onResize);        
        onResize();
        if (profitLines?.length === 0) {
            init();
        }
        return () => {
            window.removeEventListener("resize", onResize);            
        };
    }, []);

    useEffect(() => {
        if (profitLines.length > 0) return;
        init();
    }, [formData, filterData]);    

    //const handleRequestCtmp = useCallback((id: number) => {
    //    requestCtmp(keycloak.token || '', id).catch((err) => setError(err));
    //}, []);

    return (
        <>
            <Row className="">                  
                <Col className="">
                    <Card className="rounded">                        
                        <Card.Body className="">
                            <Row className="d-none d-md-block">
                                <Col>&nbsp;</Col>
                            </Row>
                            <Row className="d-none d-md-block">
                                <Col>&nbsp;</Col>
                            </Row>
                            <Row className="">
                                <Col sm="12" className="">    
                                    <h4 className="mb-2  qvend-title">{t("omzet")}</h4>                                    
                                </Col>
                            </Row>                            
                            <Row style={{ fontSize: '11px' }}>
                                <Col md={3} xs={5} style={{ paddingRight: 0 }}>
                                    <div className="bd-example">
                                        <div>
                                            <SearchInput className="form-control search" type="search" placeholder={!searchData["search"] ? "Search" : undefined } onChange={handleSearch} value={searchData.search ?? ""} />
                                        </div>                                            
                                    </div>                                    
                                </Col>
                                <Col md={3} xs={1}>
                                    <button type="button" className="btn btn-qvend" style={{ fontSize: '11px', padding: '0.5rem 0.3rem' }}>...</button>
                                </Col>                                    
                                {/*<Col className="text-end">*/}
                                {/*    {keycloak && keycloak.hasResourceRole("r-admin") && (<CsvExport onGetData={handleCsvExport} headers={csvHeaders} separator={";"} filename={"terminals.csv"} className="btn btn-qvend" style={{ fontSize: '11px' }} />)}*/}
                                {/*    {(!keycloak || !keycloak.hasResourceRole("r-admin")) && (<button type="button" className="btn btn-qvend" style={{ fontSize: '11px' }}>Download</button>)}*/}
                                {/*</Col>*/}
                            </Row>
                            <form className="" onKeyDown={handleKeyDown}>
                                <Row>
                                    <Col md={12} xs={10} style={{ paddingRight: 0 }}>
                                        <div>
                                            <fieldset className="form-control-sm" style={{ fontSize: '11px' }}>
                                                <Form.Check className="form-check-inline">
                                                    <FormCheck.Input onChange={() =>
                                                        handleFilter("0")
                                                    } type="radio" name="radios" className="form-check-input" id="radio1" defaultChecked={filterData === "0" || filterData === ""} />
                                                    <FormCheck.Label className="form-check-label" htmlFor="radio1">{t('profit.filter.day')}</FormCheck.Label>
                                                </Form.Check>
                                                <Form.Check className="form-check-inline">
                                                    <FormCheck.Input onChange={() =>
                                                        handleFilter("1")
                                                    } type="radio" name="radios" className="form-check-input" id="radio2" defaultChecked={filterData === "1"} />
                                                    <FormCheck.Label className="form-check-label" htmlFor="radio2">{t('profit.filter.week')}</FormCheck.Label>
                                                </Form.Check>
                                                <Form.Check className="form-check-inline">
                                                    <FormCheck.Input onChange={() =>
                                                        handleFilter("2")
                                                    } type="radio" name="radios" className="form-check-input" id="radio2" defaultChecked={filterData === "2"}/>
                                                    <FormCheck.Label className="form-check-label" htmlFor="radio2">{t('profit.filter.month')}</FormCheck.Label>
                                                </Form.Check>
                                                <Form.Check className="form-check-inline">
                                                    <FormCheck.Input onChange={() =>
                                                        handleFilter("3")
                                                    } type="radio" name="radios" className="form-check-input" id="radio3" defaultChecked={filterData === "3"}/>
                                                    <FormCheck.Label className="form-check-label" htmlFor="radio3">{t('profit.filter.year')}</FormCheck.Label>
                                                </Form.Check>
                                                {((siteFilter && siteFilter.length > 0) || (siteFilterQuery && siteFilterQuery.length > 2)) ? <span className="text-end">
                                                    <Badge pill bg="dark" className="p-1 " onClick={() => { setSiteFilter([]); setSiteFilterQuery(""); setProfitLines([]); setCurrentPage(0); formData === "" ? setFormData(" ") : setFormData(""); window.localStorage.removeItem("searchDataSites") }}><CloseButton variant="white" /> Sites</Badge></span> : null}
                                            </fieldset>
                                        </div>
                                    </Col>
                                </Row>
                            </form>
                            <Row className="">
                                <Col sm="12" xs="12" className="mt-2">
                                    <RefreshPull isRefreshing={isRefreshing} onSetPage={handlePaging} />
                                    <div className="table-responsive-lg" style={{ overflowX: 'hidden' }}>
                                        <div>
                                            <Row key='a' className="qvend-table-font" style={{ background: "#2b2e83", color: "#FFFFFF", overflowY: "scroll"  }}>
                                                <Col md={5} xs={3} className="qvend-table table-column-first">{t('profit.period')}</Col>
                                                <Col md={2} xs={3} className="qvend-table">{t('profit.succeededCount')}</Col>
                                                <Col md={2} xs={3} className="qvend-table">{t('profit.failedCount')}</Col>
                                                <Col md={3} xs={3} className="qvend-table">{t('profit.totalAmount')}</Col>
                                            </Row>
                                        </div>
                                        <div className="vertical-scrollable" style={{ overscrollBehaviorY: 'contain' }}>
                                            <Row key='b' className={("qvend-table-font")} style={{ background: "#cfd5ea", color: "black" }}>
                                                <Col md={5} xs={3} className="qvend-table search-in-row-first"><SearchInput className="form-control period" type="search" onChange={handleSearchColumn1} value={searchData.period ?? ""} /></Col>
                                                <Col md={2} xs={3} className="qvend-table search-in-row-padding"><SearchInput className="form-control succeded" type="search" onChange={handleSearchColumn2} value={searchData.succeeded ?? ""} /></Col>
                                                <Col md={2} xs={3} className="qvend-table search-in-row-padding"><SearchInput className="form-control failed" type="search" onChange={handleSearchColumn3} value={searchData.failed ?? ""} /></Col>
                                                <Col md={3} xs={3} className="qvend-table search-in-row-padding"><SearchInput className="form-control profitInCents" type="search" onChange={handleSearchColumn4} value={searchData.profitInCents ?? ""} /></Col>
                                            </Row>
                                            <Row className="d-none errorMsg">
                                                <Col>
                                                    {
                                                        errorMsg.map((item, index) => {
                                                            return <Alert key={index} variant="warning" className="alert" role="alert">
                                                                <span>{item}</span>
                                                            </Alert>;
                                                        })
                                                    }
                                                </Col>
                                            </Row>
                                            {profitLines.map((profitLine: Profit, index) => (                                                
                                                <ProfitItem
                                                    key={index}
                                                    profit={profitLine}                                                       
                                                    rowColor={index % 2 ? "#cfd5ea" : "#e9ebf5"}
                                                />                                               
                                            ))}
                                            {                                                
                                                //Array.from({ ...dummyTerminal, length: (tableBodyHeight / 16.5) - terminals.length } as unknown as Terminal[]).map((terminal: Terminal, index) => (
                                                //    <TerminalItem
                                                //        key={index + terminals.length}
                                                //        terminal={dummyTerminal}
                                                //        rowColor={index % 2 ? "#cfd5ea" : "#e9ebf5"}
                                                //    />
                                                //))
                                            }
                                        </div>
                                    </div>
                                </Col>                              
                            </Row>
                            <ListPagination
                                articlesCount={profitLinesCount}
                                currentPage={currentPage}
                                onSetPage={handlePaging}
                                onRefresh={handleRefreshButton} />
                        </Card.Body>
                    </Card>
                </Col>                                    
            </Row>
        </>
    )
}

export default ProfitView